import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react'
import { Button, Card, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui'
import { MultiSelect } from '@/components/ui/multi-select'
import { Switch } from '@/components/ui/switch'
import { useProjectTeamMembers } from '@/hooks/useProjects'
import { useAzureDevOpsProcessConfig } from '@/hooks/useWorkItems'
import { useChains } from '@/hooks/useChains'
import {
  useAzureDevOpsCommonWorkItemTypeFields,
  useAzureDevOpsIngestionSetting,
  useAzureDevOpsTeams,
  useSaveAzureDevOpsIngestionSetting,
} from '@/hooks/useAzureDevOpsIngestion'
import { getErrorMessage } from '@/api/errors'
import { describeIngestionChainProblem } from '@/lib/ingestionChainProblem'
import { formatDateTime } from '@/lib/timeFormatters'
import { extractValidationMessage } from '@/lib/validationErrors'
import type { AzureDevOpsIngestionSetting } from '@/types'

interface FormValues {
  isEnabled: boolean
  teamId: string
  triggerFieldReferenceName: string
  monitoredWorkItemTypes: string[]
  ownerUserId: string
  chainDefinitionId: string
}

interface AzureDevOpsIngestionSectionProps {
  projectId: string
}

const NO_CHAIN = 'none'
const ACTIVE_CHAINS = { search: '', status: 'active', type: 'all', category: 'all' } as const

const CONNECTOR_GUIDANCE = 'Azure DevOps teams and fields are read through your own Azure DevOps connector on this project. Connect it and try again.'

function toFormValues(setting: AzureDevOpsIngestionSetting | null): FormValues {
  return {
    isEnabled: setting?.isEnabled ?? false,
    teamId: setting?.teamId ?? '',
    triggerFieldReferenceName: setting?.triggerFieldReferenceName ?? '',
    monitoredWorkItemTypes: setting?.monitoredWorkItemTypes ?? [],
    ownerUserId: setting?.ownerUserId ?? '',
    chainDefinitionId: setting?.chainDefinitionId ?? '',
  }
}

function storedChainLabel(setting: AzureDevOpsIngestionSetting | null | undefined): string {
  const name = setting?.chainDisplayName ?? 'Previously selected chain'
  if (setting?.chainState === 'Missing') return 'Previously selected chain (deleted)'
  if (setting?.chainState === 'Inactive') return `${name} (inactive)`
  return name
}

interface SelectOption {
  value: string
  label: string
}

// Radix Select clears a controlled value when no item matches it yet, which happens while the lookup lists are still loading.
function ignoreEmpty(onChange: (value: string) => void) {
  return (value: string) => {
    if (value) onChange(value)
  }
}

function withSelected(options: SelectOption[], value: string, label: string | undefined): SelectOption[] {
  if (!value || options.some(option => option.value === value)) return options
  return [...options, { value, label: label || value }]
}

function ErrorStrip({ testId, children }: { testId: string; children: React.ReactNode }) {
  return (
    <p role="alert" className="flex items-start gap-2 rounded border border-danger-foreground bg-danger px-4 py-3 text-sm text-danger-foreground" data-testid={testId}>
      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
      <span>{children}</span>
    </p>
  )
}

export function AzureDevOpsIngestionSection({ projectId }: AzureDevOpsIngestionSectionProps) {
  const { data: setting, isLoading: isSettingLoading, error: settingError } = useAzureDevOpsIngestionSetting(projectId)
  const { data: teams = [], isLoading: isTeamsLoading, error: teamsError } = useAzureDevOpsTeams(projectId)
  const { data: processConfig } = useAzureDevOpsProcessConfig(projectId, true)
  const { data: members = [] } = useProjectTeamMembers(projectId)
  const { data: activeChains, isLoading: isChainsLoading, error: chainsError } = useChains(projectId, undefined, ACTIVE_CHAINS)
  const saveSetting = useSaveAzureDevOpsIngestionSetting(projectId)
  const [saveError, setSaveError] = useState<string | null>(null)

  const { control, handleSubmit, reset, getFieldState, getValues, setValue, formState: { isSubmitting } } = useForm<FormValues>({
    defaultValues: toFormValues(null),
  })

  useEffect(() => {
    if (setting !== undefined) reset(toFormValues(setting), { keepDirtyValues: true })
  }, [setting, reset])

  useEffect(() => {
    const defaultType = processConfig?.defaultRequirementType
    if (setting !== null || !defaultType) return
    if (getFieldState('monitoredWorkItemTypes').isDirty || getValues('monitoredWorkItemTypes').length > 0) return
    setValue('monitoredWorkItemTypes', [defaultType], { shouldDirty: true })
  }, [setting, processConfig, getFieldState, getValues, setValue])

  const selectedTypes = useWatch({ control, name: 'monitoredWorkItemTypes' })
  const { fields, isLoading: isFieldsLoading, error: fieldsError } = useAzureDevOpsCommonWorkItemTypeFields(projectId, selectedTypes)
  const selectedTriggerField = useWatch({ control, name: 'triggerFieldReferenceName' })
  const isTriggerFieldOutsideSelectedTypes =
    !!selectedTriggerField && !isFieldsLoading && !fieldsError && fields.length > 0
    && !fields.some(f => f.referenceName === selectedTriggerField)

  const typeOptions = useMemo(() => {
    const known = new Set([
      ...(processConfig?.requirementTypes ?? []),
      ...(processConfig?.featureTypes ?? []),
      ...(processConfig?.epicTypes ?? []),
      ...(setting?.monitoredWorkItemTypes ?? []),
    ])
    return [...known].sort().map(type => ({ value: type, label: type }))
  }, [processConfig, setting])

  const activeMembers = useMemo(() => members.filter(member => member.status === 'Active'), [members])
  const chainOptions = useMemo(
    () => (activeChains?.items ?? []).map(chain => ({ value: chain.id, label: chain.displayName })),
    [activeChains],
  )
  const chainProblem = setting?.isEnabled
    ? describeIngestionChainProblem({
      chainState: setting.chainState,
      chainDisplayName: setting.chainDisplayName,
      chainStartsSuspended: !!setting.chainStartsSuspendedAtUtc,
    })
    : null

  const onSubmit = async (values: FormValues) => {
    setSaveError(null)
    const team = teams.find(t => t.id === values.teamId)
    const field = fields.find(f => f.referenceName === values.triggerFieldReferenceName)

    try {
      await saveSetting.mutateAsync({
        isEnabled: values.isEnabled,
        teamId: values.teamId,
        teamName: team?.name ?? (values.teamId === setting?.teamId ? setting?.teamName : undefined) ?? values.teamId,
        triggerFieldReferenceName: values.triggerFieldReferenceName,
        triggerFieldDisplayName: field?.name ?? (values.triggerFieldReferenceName === setting?.triggerFieldReferenceName ? setting?.triggerFieldDisplayName : undefined) ?? values.triggerFieldReferenceName,
        monitoredWorkItemTypes: values.monitoredWorkItemTypes,
        ownerUserId: values.ownerUserId || undefined,
        chainDefinitionId: values.chainDefinitionId || null,
      })
      reset(values)
      toast.success('Azure DevOps ingestion settings saved.')
    } catch (err) {
      setSaveError(extractValidationMessage(err) ?? getErrorMessage(err) ?? 'Failed to save the ingestion settings.')
    }
  }

  if (isSettingLoading) {
    return (
      <Card className="p-6 flex items-center justify-center" data-testid="ado-ingestion-section">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
      </Card>
    )
  }

  if (settingError) {
    return (
      <Card className="p-6" data-testid="ado-ingestion-section">
        <ErrorStrip testId="ado-ingestion-load-error">
          The Azure DevOps ingestion settings could not be loaded. {getErrorMessage(settingError)}
        </ErrorStrip>
      </Card>
    )
  }

  return (
    <Card className="p-6 flex flex-col gap-5" data-testid="ado-ingestion-section">
      <div>
        <h2 className="text-xl font-bold mb-1">Azure DevOps Ingestion</h2>
        <p className="text-sm text-muted-foreground">
          Create a backlog item automatically when a work item is created in the monitored Azure DevOps team with the trigger field populated.
          Only the value present when the work item is first saved counts — filling the field in later does not bring the item in.
          Items are linked back by their Azure DevOps id and are never updated afterwards.
        </p>
      </div>

      {chainProblem && <ErrorStrip testId="ado-ingestion-chain-warning">{chainProblem}</ErrorStrip>}

      {teamsError && (
        <ErrorStrip testId="ado-ingestion-lookup-error">
          {CONNECTOR_GUIDANCE} {getErrorMessage(teamsError)}
        </ErrorStrip>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
        <div className="flex items-center justify-between rounded-lg border bg-page px-4 py-3">
          <div className="flex flex-col gap-0.5">
            <Label htmlFor="ado-ingestion-enabled" className="text-sm font-medium">Ingestion enabled</Label>
            <span className="text-xs text-muted-foreground">Azure DevOps is checked every few minutes while this is on.</span>
          </div>
          <Controller
            control={control}
            name="isEnabled"
            render={({ field }) => (
              <Switch id="ado-ingestion-enabled" checked={field.value} onCheckedChange={field.onChange} />
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-5">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ado-ingestion-team">Monitored team</Label>
            <Controller
              control={control}
              name="teamId"
              render={({ field }) => (
                <Select value={field.value} onValueChange={ignoreEmpty(field.onChange)} disabled={isTeamsLoading}>
                  <SelectTrigger id="ado-ingestion-team" aria-label="Monitored team">
                    <SelectValue placeholder={isTeamsLoading ? 'Loading teams…' : 'Select a team'} />
                  </SelectTrigger>
                  <SelectContent>
                    {withSelected(teams.map(team => ({ value: team.id, label: team.name })), field.value, field.value === setting?.teamId ? setting?.teamName : undefined).map(option => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <span className="text-xs text-muted-foreground">Teams of the Azure DevOps project connected to this project.</span>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ado-ingestion-types">Monitored work item types</Label>
            <Controller
              control={control}
              name="monitoredWorkItemTypes"
              render={({ field }) => (
                <MultiSelect
                  id="ado-ingestion-types"
                  options={typeOptions}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Select work item types"
                />
              )}
            />
            <span className="text-xs text-muted-foreground">Other types in the team are ignored.</span>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ado-ingestion-trigger-field">Trigger field</Label>
            <Controller
              control={control}
              name="triggerFieldReferenceName"
              render={({ field }) => (
                <Select value={field.value} onValueChange={ignoreEmpty(field.onChange)} disabled={isFieldsLoading || selectedTypes.length === 0}>
                  <SelectTrigger id="ado-ingestion-trigger-field" aria-label="Trigger field">
                    <SelectValue placeholder={isFieldsLoading ? 'Loading fields…' : selectedTypes.length === 0 ? 'Select work item types first' : 'Select a field'} />
                  </SelectTrigger>
                  <SelectContent>
                    {withSelected(fields.map(f => ({ value: f.referenceName, label: f.name })), field.value, field.value === setting?.triggerFieldReferenceName ? setting?.triggerFieldDisplayName : undefined).map(option => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {fieldsError ? (
              <span role="alert" className="text-xs text-danger-foreground" data-testid="ado-ingestion-fields-error">
                Fields could not be loaded. {getErrorMessage(fieldsError)}
              </span>
            ) : isTriggerFieldOutsideSelectedTypes ? (
              <span role="alert" className="text-xs text-danger-foreground" data-testid="ado-ingestion-field-not-shared">
                This field does not exist on every selected work item type. Pick another field or change the types.
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">Fields shared by every selected type. A value set at creation triggers ingestion; a Yes/No field counts only when it is set to Yes.</span>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ado-ingestion-owner">Ingestion runs as</Label>
            <Controller
              control={control}
              name="ownerUserId"
              render={({ field }) => (
                <Select value={field.value} onValueChange={ignoreEmpty(field.onChange)}>
                  <SelectTrigger id="ado-ingestion-owner" aria-label="Ingestion runs as">
                    <SelectValue placeholder="Select a project member" />
                  </SelectTrigger>
                  <SelectContent>
                    {withSelected(
                      activeMembers.map(member => ({ value: member.userId, label: `${member.displayName} (${member.email})` })),
                      field.value,
                      'Previously designated user (no longer a member)',
                    ).map(option => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <span className="text-xs text-muted-foreground">Their Azure DevOps connector is used for every run; ingested items are recorded as created by them.</span>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ado-ingestion-chain">Start chain on ingestion</Label>
            <Controller
              control={control}
              name="chainDefinitionId"
              render={({ field }) => (
                <Select
                  value={field.value || NO_CHAIN}
                  onValueChange={value => { if (value) field.onChange(value === NO_CHAIN ? '' : value) }}
                  disabled={isChainsLoading}
                >
                  <SelectTrigger id="ado-ingestion-chain" aria-label="Start chain on ingestion">
                    <SelectValue placeholder={isChainsLoading ? 'Loading chains…' : 'None'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CHAIN}>None</SelectItem>
                    {withSelected(chainOptions, field.value, storedChainLabel(setting)).map(option => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {chainsError ? (
              <span role="alert" className="text-xs text-danger-foreground" data-testid="ado-ingestion-chains-error">
                Chains could not be loaded, so the chain cannot be changed. {getErrorMessage(chainsError)}
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">
                Starts one run of this chain for every item ingested from now on, as the user above, with the item as its workItemId input.
              </span>
            )}
          </div>
        </div>

        {setting && (
          <div className="border-t pt-4 flex flex-col gap-3" data-testid="ado-ingestion-status">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</span>
            <dl className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <dt className="text-xs text-muted-foreground">Last run</dt>
                <dd>{formatDateTime(setting.lastPolledAtUtc)}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Last successful run</dt>
                <dd className="inline-flex items-center gap-1.5">
                  {setting.lastSuccessfulPollAtUtc && <CheckCircle2 className="h-3.5 w-3.5 text-success-foreground" aria-hidden />}
                  {formatDateTime(setting.lastSuccessfulPollAtUtc)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Ingesting items created after</dt>
                <dd>{formatDateTime(setting.ingestFromUtc)}</dd>
              </div>
              {setting.chainDefinitionId && (
                <div>
                  <dt className="text-xs text-muted-foreground">Last chain start</dt>
                  <dd data-testid="ado-ingestion-last-chain-start">{formatDateTime(setting.lastChainStartAtUtc)}</dd>
                </div>
              )}
            </dl>
            {setting.lastError && (
              <ErrorStrip testId="ado-ingestion-last-error">The last run failed: {setting.lastError}</ErrorStrip>
            )}
            {setting.lastChainStartError && (
              <ErrorStrip testId="ado-ingestion-chain-start-error">
                A chain could not be started ({formatDateTime(setting.lastChainStartErrorAtUtc)}): {setting.lastChainStartError}
              </ErrorStrip>
            )}
            {setting.awaitingInputsAutoRunCount > 0 && (
              <Link
                to={`/projects/${projectId}/workflows/runs?tab=chains&status=AwaitingInputs&trigger=AzureDevOpsIngestion`}
                className="text-sm underline hover:text-foreground"
                data-testid="ado-ingestion-awaiting-inputs-link"
              >
                {setting.awaitingInputsAutoRunCount} automatically started chain run(s) waiting for inputs
              </Link>
            )}
          </div>
        )}

        {saveError && <ErrorStrip testId="ado-ingestion-save-error">{saveError}</ErrorStrip>}

        <div className="flex justify-end border-t pt-4">
          <Button type="submit" disabled={isSubmitting || saveSetting.isPending || isTeamsLoading || isFieldsLoading}>
            {saveSetting.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Card>
  )
}
